import "./btn-workspace-dropdown.css"
import cn from "classnames"
import { useTranslation } from "@nova/use-translation"
import React, { Dispatch, SetStateAction } from "react"
import * as Icons from "@nova/component-icons"
import { RadioButton } from "./btn"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"

type RadioButton = {
    label: string
    isSelected: boolean
    onClick: () => void
}

type RadioSection = {
    options: RadioButton[]
    title: string
}

type ButtonSplitProps = React.HTMLAttributes<HTMLElement> & {
    sections: RadioSection[]
    label: string
    labelIcon?: Icons.IconComponent
    disabled?: boolean
    menuOpen: boolean
    setMenuOpen: Dispatch<SetStateAction<boolean>>
    isSmall?: boolean
}

/**
 *
 * Restyled ButtonSplit introduced with Workspace Next Steps
 */
export const ButtonRadioDropdown = ({
    label,
    labelIcon,
    className,
    sections,
    disabled,
    menuOpen,
    setMenuOpen,
    style,
    isSmall,
    ...rest
}: ButtonSplitProps) => {
    const { t } = useTranslation()

    return (
        <ClickAwayListenerV2 onClickAway={() => setMenuOpen(false)}>
            <div
                className={cn(
                    "relative block bg-gray-700 cursor-pointer rounded-lg text-gray-0 ",
                    {
                        "!text-gray-300 !cursor-not-allowed": disabled,
                    },
                    className
                )}
                style={style}
                {...rest}
            >
                <div
                    className={cn(
                        "flex w-full justify-between items-center p-1 pl-4 text-grey-0 border-[0.063rem] border-transparent rounded-lg overflow-hidden whitespace-nowrap",
                        { "border-blue-200": menuOpen }
                    )}
                    onClick={() => {
                        setMenuOpen((prev) => !prev)
                    }}
                >
                    <div className="flex items-center gap-1 w-full">
                        {labelIcon && (
                            <Icons.SmallIcon Icon={labelIcon} className="text-gray-100" />
                        )}
                        {!isSmall && (
                            <h5 className="button-workspace-dropdown capitalize max-w-[90%] overflow-hidden text-ellipsis select-none">
                                {t(label)}
                            </h5>
                        )}
                        {isSmall && (
                            <small className="button-workspace-dropdown capitalize max-w-[90%] overflow-hidden text-ellipsis select-none">
                                {t(label)}
                            </small>
                        )}
                    </div>
                    <Icons.ArrowDropDownIcon
                        dropdownOpen={menuOpen}
                        setDropdownOpen={() => {}}
                        disabled={disabled}
                    />
                </div>

                <DropdownComponent menuOpen={menuOpen} sections={sections} />
            </div>
        </ClickAwayListenerV2>
    )
}

const DropdownComponent = ({
    menuOpen,
    sections,
    style,
}: Omit<ButtonSplitProps, "label" | "onClick" | "setMenuOpen">) => {
    return (
        <div
            className={cn(
                "absolute z-50 w-[188px] flex flex-col items-start rounded-md bg-gray-900 pb-4 pt-2 mt-1 ",
                {
                    visible: menuOpen,
                    hidden: !menuOpen,
                }
            )}
            style={{
                ...style,
                boxShadow: "0 2px 10px 0 rgba(12, 12, 15, 0.95)" /* shadow-context-menu */,
            }}
        >
            {sections.map((section, idx) => {
                return (
                    <div className="w-full">
                        <h5 className="text-gray-300 px-4 pt-2 pb-1 select-none">
                            {section.title}
                        </h5>
                        {section.options.map((option) => {
                            return (
                                <div
                                    className="flex items-center gap-2 py-1.5 px-4 w-full cursor-pointer hover:bg-gray-700"
                                    onClick={option.onClick}
                                >
                                    <RadioButton
                                        isSelected={option.isSelected}
                                        onClick={() => {}}
                                    />
                                    <h5 className="text-gray-0 select-none">{option.label}</h5>
                                </div>
                            )
                        })}
                        {idx + 1 !== sections.length && (
                            <div className="border-b border-gray-700 mt-2" />
                        )}
                    </div>
                )
            })}
        </div>
    )
}
